import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { getBrokerDashboardRenewalsSummary } from '../../../../../api/requests';
import { useQuery } from '@tanstack/react-query';
import { formatCurrencyAmount, formatNumberValue, getMonthNameFromDate } from '../../../../../utils/miscFunctions';
import DatePickerInput from '../../../../../components/form-control/DatePickerInput';
import { Cell, Pie, PieChart, ResponsiveContainer, Sector, Tooltip } from 'recharts';
import { getSelectedCountry } from '../../../../../states/slices/resourceSlice';
import { useSelector } from 'react-redux';
import useLayoutHook from '../../../../../hooks/layoutHook';

const ActiveShape: React.FC<{
    cx?: number;
    cy?: number;
    innerRadius: number;
    outerRadius: number;
    startAngle: number;
    endAngle: number;
    fill: string;
}> = ({ cx = 0, cy = 0, innerRadius, outerRadius, startAngle, endAngle, fill }) => (
    <g>
        <Sector
            cx={cx}
            cy={cy}
            innerRadius={innerRadius}
            outerRadius={outerRadius + 6}
            startAngle={startAngle}
            endAngle={endAngle}
            fill={fill}
        />
        <Sector
            cx={cx}
            cy={cy}
            startAngle={startAngle}
            endAngle={endAngle}
            innerRadius={outerRadius + 8}
            outerRadius={outerRadius + 10}
            fill={fill}
        />
    </g>
);

interface RenewalDistributionChartProps {
    // When an admin views a company, scope renewals to that company instead of the logged-in user.
    companyId?: number;
}

const RenewalDistributionChart: React.FC<RenewalDistributionChartProps> = ({ companyId }) => {
    const [activeTab, setActiveTab] = useState<IMetricType>('ytd');
    const { screenWidth } = useLayoutHook();
    const [selectedYearMonth, setSelectedYearMonth] = useState<Date>(new Date());
    const [selectedYear, setSelectedYear] = useState<Date>(new Date());
    const country = useSelector(getSelectedCountry)
    const selectedCountryIso = country.currencyIso

    const handleTabChange = (tab: IMetricType) => {
        setActiveTab(tab);
    };

    const activeButtonClassNames = ` text-black bg-[#FBC02D] shadow px-3 py-1 text-sm font-medium rounded-full w-full whitespace-nowrap`;
    const inActiveButtonClassNames = 'text-[#5B5B5B] px-3 py-1 text-sm font-medium rounded-full w-full whitespace-nowrap';

    const fetchBrokerDashboardRenewalsSummary = async () => {
        if (!selectedYear || !selectedYearMonth) return;
        const year = activeTab === 'ytd' ? selectedYear.getFullYear() : selectedYearMonth.getFullYear();
        // JS Date.getMonth() is 0-indexed (0 = January); the backend expects a
        // 1-indexed month (1-12). Without the +1 every month was off by one and
        // January (0) made the API throw "Invalid value for MonthOfYear: 0" (500),
        // so the monthly renewal metrics were wrong or blank.
        const month = activeTab === 'ytd' ? undefined : selectedYearMonth.getMonth() + 1;

        try {
            const res: any = await getBrokerDashboardRenewalsSummary(year, month, undefined, companyId);
            if (res) {
                const data = res?.data;
                return data;
            }
        } catch (error) {
            console.error(error);
        }
    };

    const { data: renewalSummaryData, isLoading: isLoadingRenewalSummary } = useQuery({
        queryKey: ['fetchBrokerDashboardRenewalsSummary', selectedYearMonth, selectedYear, companyId],
        queryFn: fetchBrokerDashboardRenewalsSummary,
        // Refetch on mount so the dashboard reflects edits without a full reload,
        // overriding the app-wide 5-min staleTime (src/lib/queryClient.ts).
        refetchOnMount: 'always',
    });

    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="bg-white dark:bg-gray-900 p-4 border border-gray-200 dark:border-gray-700 shadow-md rounded-md">
                    <p className="font-medium text-gray-900 dark:text-white">{data.type}</p>
                    <p className="text-sm text-gray-600 dark:text-white">Count: {formatNumberValue(data.count)}</p>
                    <p className="text-sm text-gray-600 dark:text-white">Value: {formatCurrencyAmount(data.value)}</p>
                    <p className="text-sm text-gray-600 dark:text-white">Share: {data.percentage?.toFixed(2) || '0.0'}%</p>
                </div>
            );
        }
        return null;
    };

    // Transform the data for the chart
    const finalChartData = React.useMemo(() => {
        if (!renewalSummaryData) return [];

        const summary = activeTab === 'ytd' ? renewalSummaryData?.ytd : renewalSummaryData?.monthly;

        if (!summary) return [];

        // Convert renewal summary data to chart format
        const renewalCategories = [
            {
                type: 'Renewed',
                count: summary?.policiesRenewed || 0,
                value: summary?.renewalAmountCollected || 0,
                color: '#22c55e' // green-500
            },
            {
                type: 'Lost',
                count: summary?.policiesLost || 0,
                value: summary?.renewalAmountLost || 0,
                color: '#ef4444' // red-500
            },
            {
                type: 'Pending',
                count: summary?.policiesPending || 0,
                value: 0,
                color: '#f59e0b' // amber-500
            }
        ];

        const totalCount = renewalCategories.reduce((sum, item) => sum + item.count, 0);

        return renewalCategories.map((item, index) => ({
            ...item,
            percentage: totalCount > 0 ? (item.count / totalCount) * 100 : 0
        }));
    }, [renewalSummaryData, activeTab]);

    // Memoized calculations
    const totalPolicies = React.useMemo(() =>
        finalChartData.reduce((sum: number, item: any) => sum + item.count, 0),
        [finalChartData]
    );

    const totalValue = React.useMemo(() =>
        finalChartData.reduce((sum: number, item: any) => sum + item.value, 0),
        [finalChartData]
    );

    const hasData = finalChartData && finalChartData?.length > 0 && finalChartData?.some((item: any) => item.count > 0);

    // Reusable loading state component
    const LoadingState = () => (
        <div className='h-full flex flex-col bg-white dark:bg-darkBg rounded-2xl'>
            <div className="flex justify-between items-center mb-6">
                <div>
                    <div className="h-6 bg-gray-200 rounded w-48 animate-pulse mb-2"></div>
                    <div className="h-4 bg-gray-100 rounded w-32 animate-pulse"></div>
                </div>
                <div className="w-32 h-8 bg-gray-100 rounded-lg animate-pulse"></div>
            </div>

            <div className="flex-1 flex flex-col">
                <div className=" mb-4 flex items-center justify-center">
                    <div className="relative">
                        <div className="w-32 h-32 border-8 border-gray-200 rounded-full animate-pulse"></div>
                        <div className="absolute inset-4 w-24 h-24 border-4 border-gray-100 rounded-full animate-pulse"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full animate-pulse"></div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-4 mb-4">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="flex items-center">
                            <div className="w-3 h-3 bg-gray-200 rounded-full mr-2 animate-pulse"></div>
                            <div className="h-3 bg-gray-100 rounded w-16 animate-pulse"></div>
                        </div>
                    ))}
                </div>

                <div className="mt-auto pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-3 gap-4">
                    <div className="px-0 py-3 bg-gray-100 rounded-lg col-span-1 animate-pulse">
                        <div className="h-3 bg-gray-200 rounded w-20 mx-auto mb-1"></div>
                        <div className="h-5 bg-gray-300 rounded w-12 mx-auto"></div>
                    </div>
                    <div className="px-0 py-4 bg-gray-100 rounded-lg col-span-2 animate-pulse">
                        <div className="h-3 bg-gray-200 rounded w-24 mx-auto mb-1"></div>
                        <div className="h-5 bg-gray-300 rounded w-16 mx-auto"></div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Reusable empty state component
    const EmptyState = ({ datePickerProps }: { datePickerProps: any }) => (
        <div className="h-full flex flex-col bg-white dark:bg-darkBg rounded-2xl">
            <div className="w-32 mb-6">
                <DatePickerInput {...datePickerProps} />
            </div>

            <div className="flex-1 flex items-center justify-center">
                <div className="text-center max-w-md">
                    <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-full flex items-center justify-center">
                        <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText mb-2">
                        No Renewal Data Found
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                        There are no renewal records available for {activeTab === 'ytd' ? selectedYear.getFullYear() : (getMonthNameFromDate((selectedYearMonth.getMonth() + 1).toString())) + '-' + selectedYearMonth.getFullYear()}.
                        Try selecting a different date range or check back later for updates.
                    </p>
                    <div className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Try a different date range
                    </div>
                </div>
            </div>
        </div>
    );

    // Date picker configurations
    const datePickerConfigs = React.useMemo(() => ({
        ytd: {
            id: "month",
            selected: selectedYear,
            onChange: (date: Date | null) => date && setSelectedYear(date),
            showYearOnly: true,
            dateFormat: "yyyy",
            allowFutureSelections: false,
            classes: "w-full !w-[120px]"
        },
        monthly: {
            id: "month",
            selected: selectedYearMonth,
            onChange: (date: Date | null) => date && setSelectedYearMonth(date),
            showMonthYearPicker: true,
            dateFormat: "MMM yyyy",
            allowFutureSelections: false,
            classes: "w-full !w-[120px]"
        }
    }), [selectedYear, selectedYearMonth]);

    // Reusable chart component
    const ChartContent = ({ datePickerProps }: { datePickerProps: any }) => {
        // Handle loading state
        if (isLoadingRenewalSummary) {
            return <LoadingState />;
        }

        // Handle empty state
        if (!hasData) {
            return <EmptyState datePickerProps={datePickerProps} />;
        }

        // Main chart content
        return (
            <>
                <div className="w-40">
                    <DatePickerInput {...datePickerProps} />
                </div>

                <div className="flex-1 flex flex-col">
                    <div className="h-64 mb-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    activeShape={ActiveShape as any}
                                    data={finalChartData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    paddingAngle={2}
                                    dataKey="count"
                                >
                                    {finalChartData.map((entry: any, index: number) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={entry.color}
                                            fillOpacity={0.8}
                                            stroke="#fff"
                                            strokeWidth={1}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="flex flex-wrap items-center justify-center gap-4 mb-4">
                        {finalChartData.map((entry: any, index: number) => (
                            <div key={`legend-${index}`} className="flex items-center">
                                <span
                                    className="w-3 h-3 rounded-full mr-2"
                                    style={{ backgroundColor: entry.color }}>
                                </span>
                                <span className="text-xs text-gray-600 dark:text-darkBg-mainText">
                                    {entry.type}: {formatNumberValue(entry.count)}
                                </span>
                            </div>
                        ))}
                    </div>

                    {/* <div className="mt-auto pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-3 gap-4 text-center">
                        <div className="px-0 py-3 bg-gray-50 dark:bg-gray-800 rounded-lg col-span-1">
                            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Total Policies</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">
                                {formatNumberValue(totalPolicies)}
                            </p>
                        </div>
                        <div className="px-0 py-4 bg-gray-50 dark:bg-gray-800 rounded-lg col-span-2">
                            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">{`Total Premium (${selectedCountryIso})`}</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">
                                {formatCurrencyAmount(totalValue, undefined, false)}
                            </p>
                        </div>
                    </div> */}
                </div>
            </>
        );
    };

    return (
        <div className='bg-white dark:bg-darkBg rounded-2xl shadow-sm border border-gray-200 dark:border-darkBg-border p-5'>
            <div
                className={clsx(
                    'flex items-center ',
                    { ['flex flex-row justify-between w-full ']: screenWidth > 1213 },
                    { ['flex-row space-x-2']: screenWidth <= 1213 }
                )}>
                <div className={` md:w-full pb-4 md:pb-2 text-md sm:text-md md:text-xl font-bold dark:text-darkBg-mainText `}>
                    Renewal Metrics
                </div>
                <div className={` flex items-center space-x-2 bg-gray-100 p-1 rounded-full`}>
                    <button
                        className={` ${activeTab === 'ytd' ? activeButtonClassNames : inActiveButtonClassNames
                            }`}
                        onClick={() => handleTabChange('ytd')}>
                        YTD
                    </button>
                    <button
                        className={`rounded-full ${activeTab === 'monthly' ? activeButtonClassNames : inActiveButtonClassNames
                            }`}
                        onClick={() => handleTabChange('monthly')}>
                        Monthly
                    </button>
                </div>
            </div>

            <div className="">
                {activeTab === 'ytd' &&
                    <ChartContent datePickerProps={datePickerConfigs.ytd} />
                }

                {activeTab === 'monthly' &&
                    <ChartContent datePickerProps={datePickerConfigs.monthly} />
                }
            </div>
        </div>
    );
};

export default RenewalDistributionChart;


interface TabProps {
    name: string;
    children: React.ReactNode;
}

type IMetricType = 'ytd' | 'monthly';